import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { NATURE_CONCERNE } from "@/lib/concerned";

/**
 * Filtre sur le tiers concerné par un litige.
 *
 * Une seule entrée pour trois natures : le tiers mis en cause vit dans
 * `client_id`, `supplier_id` ou `carrier_id` selon la typologie, et
 * l'utilisateur ne raisonne pas en champs — il cherche « les litiges de DUNI ».
 *
 * Le filtrage se fait CÔTÉ SERVEUR (`concerned_id`) : un filtrage local ne
 * verrait jamais au-delà des lignes déjà chargées.
 *
 * Un compte commercial ne se voit proposer que des clients. `supplier.read` lui
 * est fermé : peupler la liste avec des fournisseurs lui livrerait l'annuaire
 * complet des tiers par un canal détourné.
 */
export default function ConcernedFilter({ value, onChange, commercial = false }) {
  const [ouvert, setOuvert] = useState(false);
  const [saisie, setSaisie] = useState("");
  const [entites, setEntites] = useState([]);
  const [chargement, setChargement] = useState(false);

  useEffect(() => {
    if (!ouvert) return undefined;
    let annule = false;
    setChargement(true);

    // Anti-rebond : la recherche part au serveur, une requête par frappe serait
    // inutilement coûteuse au-delà de quelques centaines de fiches.
    const minuteur = setTimeout(async () => {
      try {
        const requetes = [api.get("/clients", { params: { search: saisie || undefined } })];
        if (!commercial) {
          requetes.push(api.get("/suppliers", { params: { search: saisie || undefined, limit: 200 } }));
        }
        const reponses = await Promise.all(requetes);
        if (annule) return;

        const clients = (reponses[0].data || []).map((c) => ({
          id: c.id, nom: c.name, nature: "client",
        }));
        const tiers = (reponses[1]?.data || []).map((s) => ({
          id: s.id,
          nom: s.name,
          nature: s.kind === "transporteur" ? "transporteur" : "fournisseur",
        }));
        setEntites([...clients, ...tiers]);
      } catch {
        if (!annule) setEntites([]);
      } finally {
        if (!annule) setChargement(false);
      }
    }, saisie ? 300 : 0);

    return () => {
      annule = true;
      clearTimeout(minuteur);
    };
  }, [ouvert, saisie, commercial]);

  const selection = useMemo(
    () => entites.find((e) => e.id === value) || null,
    [entites, value],
  );

  const libelle = selection ? selection.nom : "Tous concernés";

  return (
    <Popover open={ouvert} onOpenChange={setOuvert}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={ouvert}
          className="h-10 w-[190px] justify-between rounded-xl border-slate-200 font-normal"
          data-testid="filter-concerned"
        >
          <span className={cn("truncate", !selection && "text-slate-500")}>{libelle}</span>
          {value ? (
            <X
              size={14}
              className="ml-1 shrink-0 text-slate-400 hover:text-rose-600"
              role="button"
              aria-label="Retirer le filtre"
              data-testid="filter-concerned-clear"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            />
          ) : (
            <ChevronsUpDown size={14} className="ml-1 shrink-0 text-slate-400" />
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[280px] p-0" align="start">
        <div className="flex items-center gap-2 border-b border-slate-100 px-3">
          <Search size={14} className="shrink-0 text-slate-400" />
          <Input
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            placeholder="Rechercher un tiers…"
            className="h-10 border-0 px-0 shadow-none focus-visible:ring-0"
            data-testid="filter-concerned-search"
          />
        </div>

        <div className="max-h-64 overflow-y-auto py-1">
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setOuvert(false);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
            data-testid="filter-concerned-all"
          >
            <Check size={14} className={cn("shrink-0", value ? "opacity-0" : "text-blue-600")} />
            Tous concernés
          </button>

          {chargement ? (
            <p className="px-3 py-4 text-center text-xs text-slate-400">Chargement…</p>
          ) : entites.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-slate-400">
              {saisie ? "Aucun tiers ne correspond." : "Aucun tiers enregistré."}
            </p>
          ) : (
            entites.map((entite) => (
              <button
                key={`${entite.nature}-${entite.id}`}
                type="button"
                onClick={() => {
                  onChange(entite.id);
                  setOuvert(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                data-testid={`filter-concerned-option-${entite.id}`}
              >
                <Check
                  size={14}
                  className={cn(
                    "shrink-0",
                    value === entite.id ? "text-blue-600" : "opacity-0",
                  )}
                />
                <span className="truncate">{entite.nom}</span>
                {/* Deux tiers peuvent porter le même nom dans deux collections :
                    la nature doit rester lisible pour les distinguer. */}
                <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wider text-slate-400">
                  {NATURE_CONCERNE[entite.nature]}
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
